import React, { useState } from 'react';
import { ChevronLeft, TrendingUp, Activity, Bug, BarChart2 } from 'lucide-react';
interface ExerciseAnalyticsProps {
  exerciseName: string;
  onBack: () => void;
}
type AnalyticsState = 'normal' | 'no-history' | 'single-session' | 'no-e1rm';
export function ExerciseAnalytics({
  exerciseName,
  onBack
}: ExerciseAnalyticsProps) {
  const [showDebug, setShowDebug] = useState(false);
  const [analyticsState, setAnalyticsState] = useState<AnalyticsState>('normal');
  const history = [{
    date: 'Oct 14',
    weight: '60 kg',
    reps: '10, 10, 8'
  }, {
    date: 'Oct 10',
    weight: '57.5 kg',
    reps: '10, 10, 10'
  }, {
    date: 'Oct 6',
    weight: '55 kg',
    reps: '12, 10, 10'
  }, {
    date: 'Oct 2',
    weight: '55 kg',
    reps: '10, 10, 8'
  }];
  const singleSessionHistory = [{
    date: 'Oct 14',
    weight: '60 kg',
    reps: '10, 10, 8'
  }];
  const displayedHistory = analyticsState === 'single-session' ? singleSessionHistory : history;
  const showChart = analyticsState === 'normal';
  const showE1RM = analyticsState === 'normal';
  const showSingleSessionMetric = analyticsState === 'single-session';
  const showFallbackMetric = analyticsState === 'no-e1rm';
  return <div className="flex flex-col h-full w-full bg-stone-50 overflow-hidden relative" data-id="element-641">
      {/* Debug Toggle */}
      <button onClick={() => setShowDebug(!showDebug)} className="absolute top-8 right-2 z-40 w-7 h-7 rounded-full bg-slate-800/70 text-white flex items-center justify-center hover:bg-slate-800 transition-colors" title="Debug panel" data-id="element-642">
        <Bug size={14} data-id="element-643" />
      </button>

      {/* Debug Panel */}
      {showDebug && <div className="absolute top-16 right-2 z-40 bg-slate-800 text-white rounded-2xl p-3 shadow-xl w-64 text-xs animate-in fade-in zoom-in-95 duration-150 max-h-[500px] overflow-y-auto" data-id="element-644">
          <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-645">
            Debug Panel
          </p>
          <button onClick={() => {
        setAnalyticsState('normal');
        setShowDebug(false);
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-646">
            ▶ Normal analytics view
          </button>
          <button onClick={() => {
        setAnalyticsState('no-history');
        setShowDebug(false);
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-647">
            ▶ No confirmed history
          </button>
          <button onClick={() => {
        setAnalyticsState('single-session');
        setShowDebug(false);
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-648">
            ▶ Only 1 confirmed session
          </button>
          <button onClick={() => {
        setAnalyticsState('no-e1rm');
        setShowDebug(false);
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium" data-id="element-649">
            ▶ Insufficient e1RM support
          </button>
        </div>}

      {/* Header */}
      <div className="flex items-center justify-between p-6 pt-8 bg-stone-50/90 backdrop-blur-md z-20 shrink-0" data-id="element-650">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-slate-200/50 text-slate-600 transition-colors" aria-label="Go back" data-id="element-651">
          <ChevronLeft size={28} data-id="element-652" />
        </button>
        <div className="text-center" data-id="element-653">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-654">
            Analytics
          </p>
          <h1 className="text-lg font-extrabold text-slate-800" data-id="element-655">
            {exerciseName}
          </h1>
        </div>
        <div className="w-10" data-id="element-656" /> {/* Spacer */}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-12" data-id="element-657">
        {analyticsState === 'no-history' /* Empty State - No History */ ? <div className="flex flex-col items-center justify-center pt-20 px-4 animate-in fade-in duration-300" data-id="element-658">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4" data-id="element-659">
              <BarChart2 size={28} className="text-slate-400" data-id="element-660" />
            </div>

            <h2 className="text-lg font-extrabold text-slate-800 mb-2 text-center" data-id="element-661">
              No confirmed history yet for this exercise
            </h2>

            <p className="text-sm font-medium text-slate-500 text-center max-w-xs leading-relaxed" data-id="element-662">
              Complete this exercise in a workout to start seeing analytics
              here.
            </p>
          </div> : <div className="space-y-6" data-id="element-663">
            {/* Trend Summary Card - E1RM */}
            {showE1RM && <div className="bg-blue-500 rounded-3xl p-6 text-white shadow-md relative overflow-hidden" data-id="element-664">
                <div className="absolute -right-4 -top-4 opacity-20" data-id="element-665">
                  <TrendingUp size={120} data-id="element-666" />
                </div>
                <div className="relative z-10" data-id="element-667">
                  <div className="flex items-center gap-2 mb-2" data-id="element-668">
                    <Activity size={18} className="text-blue-200" data-id="element-669" />
                    <span className="text-sm font-bold text-blue-200 uppercase tracking-wider" data-id="element-670">
                      Estimated 1RM
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-671">
                    <h2 className="text-4xl font-extrabold" data-id="element-672">75</h2>
                    <span className="text-lg font-bold text-blue-200" data-id="element-673">kg</span>
                  </div>
                  <p className="text-sm font-medium text-blue-100" data-id="element-674">
                    +5 kg in the last 30 days. Great progress!
                  </p>
                </div>
              </div>}

            {/* Confirmed Performance Metric - Single Session (low-data fallback) */}
            {showSingleSessionMetric && <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-id="element-675">
                <div className="flex items-center gap-2 mb-4" data-id="element-676">
                  <div className="w-8 h-8 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center" data-id="element-677">
                    <Activity size={16} data-id="element-678" />
                  </div>
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-679">
                    Confirmed Performance
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3" data-id="element-680">
                  <div className="bg-stone-50 rounded-2xl p-3 text-center" data-id="element-681">
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-682">
                      Weight
                    </p>
                    <p className="text-xl font-extrabold text-slate-800" data-id="element-683">60</p>
                    <p className="text-xs font-bold text-slate-400" data-id="element-684">kg</p>
                  </div>
                  <div className="bg-stone-50 rounded-2xl p-3 text-center" data-id="element-685">
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-686">
                      Sets
                    </p>
                    <p className="text-xl font-extrabold text-slate-800" data-id="element-687">3</p>
                    <p className="text-xs font-bold text-slate-400" data-id="element-688">
                      completed
                    </p>
                  </div>
                  <div className="bg-stone-50 rounded-2xl p-3 text-center" data-id="element-689">
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-690">
                      Best set
                    </p>
                    <p className="text-xl font-extrabold text-slate-800" data-id="element-691">10</p>
                    <p className="text-xs font-bold text-slate-400" data-id="element-692">reps</p>
                  </div>
                </div>
                <p className="text-xs font-medium text-slate-400 mt-4 text-center leading-relaxed" data-id="element-693">
                  From your first confirmed session — more data will unlock
                  trends and estimates.
                </p>
              </div>}

            {/* Fallback Metric Card - No E1RM Support */}
            {showFallbackMetric && <div className="bg-emerald-500 rounded-3xl p-6 text-white shadow-md relative overflow-hidden" data-id="element-694">
                <div className="absolute -right-4 -top-4 opacity-20" data-id="element-695">
                  <Activity size={120} data-id="element-696" />
                </div>
                <div className="relative z-10" data-id="element-697">
                  <div className="flex items-center gap-2 mb-2" data-id="element-698">
                    <Activity size={18} className="text-emerald-200" data-id="element-699" />
                    <span className="text-sm font-bold text-emerald-200 uppercase tracking-wider" data-id="element-700">
                      Best Confirmed Working Weight
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-701">
                    <h2 className="text-4xl font-extrabold" data-id="element-702">60</h2>
                    <span className="text-lg font-bold text-emerald-200" data-id="element-703">
                      kg
                    </span>
                  </div>
                  <p className="text-sm font-medium text-emerald-100" data-id="element-704">
                    Based on your recent confirmed sessions
                  </p>
                  <p className="text-xs font-medium text-emerald-200 mt-3 pt-3 border-t border-emerald-400" data-id="element-705">
                    This exercise is tracked using confirmed workout performance
                    instead of estimated 1RM.
                  </p>
                </div>
              </div>}

            {/* Simple Progress Chart - Only if enough data */}
            {showChart && <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-id="element-706">
                <h3 className="font-extrabold text-slate-800 mb-6" data-id="element-707">
                  Volume Trend
                </h3>
                <div className="flex items-end justify-between h-32 gap-2" data-id="element-708">
                  {[40, 55, 45, 65, 60, 80, 75].map((height, i) => <div key={i} className="w-full flex flex-col items-center gap-2 group" data-id="element-709">
                      <div className="w-full bg-blue-100 rounded-t-md relative group-hover:bg-blue-200 transition-colors" style={{
                height: `${height}%`
              }} data-id="element-710">
                        {i === 6 && <div className="absolute -top-1 left-0 right-0 h-1 bg-blue-500 rounded-t-md" data-id="element-711" />}
                      </div>
                    </div>)}
                </div>
                <div className="flex justify-between mt-2 text-[10px] font-bold text-slate-400 uppercase" data-id="element-712">
                  <span data-id="element-713">Sep 15</span>
                  <span data-id="element-714">Oct 14</span>
                </div>
              </div>}

            {/* Low Data Message - Single Session */}
            {analyticsState === 'single-session' && <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex items-start gap-3" data-id="element-715">
                <TrendingUp size={18} className="text-blue-500 shrink-0 mt-0.5" data-id="element-716" />
                <div data-id="element-717">
                  <p className="text-sm font-bold text-blue-800 mb-1" data-id="element-718">
                    Complete a few more sessions to see a trend
                  </p>
                  <p className="text-xs font-medium text-blue-600 leading-relaxed" data-id="element-719">
                    Your progress chart will appear here once you have more
                    confirmed workout data.
                  </p>
                </div>
              </div>}

            {/* Recent Sessions */}
            <div data-id="element-720">
              <h3 className="font-extrabold text-slate-800 mb-4 px-2" data-id="element-721">
                Recent Sessions
              </h3>
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden" data-id="element-722">
                {displayedHistory.map((session, i) => <div key={i} className={`p-4 flex items-center justify-between ${i !== displayedHistory.length - 1 ? 'border-b border-slate-100' : ''}`} data-id="element-723">
                    <div data-id="element-724">
                      <p className="font-bold text-slate-800" data-id="element-725">{session.date}</p>
                      <p className="text-sm font-medium text-slate-500" data-id="element-726">
                        {session.reps} reps
                      </p>
                    </div>
                    <div className="text-right" data-id="element-727">
                      <p className="font-extrabold text-blue-600" data-id="element-728">
                        {session.weight}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>}
      </div>
    </div>;
}